import { inject, Injectable } from '@angular/core';

import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { BsHubService } from '../Hub/bs-hub.service';
import { FirebaseService, ProfileService } from '@brightside-web/desktop/data-access/shared';
import { Environment } from '@micro-core/environment';

export type SignInAnalyticsType = 'password' | 'sso' | 'sign up';

export const PENDING_SIGN_IN_TYPE_KEY = 'PENDING_SIGN_IN_TYPE';

/** Persists last completed/started sign-in type (localStorage; survives Auth0 redirect). */
export const LAST_SIGN_IN_TYPE_KEY = 'brightside_last_sign_in_type';

export interface LoginOptions {
  connection?: string;
  trackSignInAnalytics?: boolean;
}

export interface UserAttributesInterface {
  guid: string;
  intercom_external_id: string;
  last_name: string;
  first_name: string;
  phone_number: string;
  email: string;
  locale: string;
  company: string;
  sf_contact_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class BsAuthService {
  private _reloginErrors: string[] = ['Unknown or invalid refresh token.'];
  private _auth0Service: Auth0Service = inject(Auth0Service);
  private _bsHubService: BsHubService = inject(BsHubService);
  private _profileService: ProfileService = inject(ProfileService);
  private _analytics: FirebaseService = inject(FirebaseService);

  private _userLoggedInSubject = new BehaviorSubject<boolean>(false);
  private _userLoggedOutSubject = new BehaviorSubject<boolean>(false);

  private _defaultScope = 'openid profile email phone_number read:current_user read:current_user_metadata offline_access';

  private storedAccessToken: string;
  private overrideIsAuthenticated: boolean | null = null;
  private userId: string | undefined;

  static consumePendingSignInType(): SignInAnalyticsType | null {
    const value = sessionStorage.getItem(PENDING_SIGN_IN_TYPE_KEY);
    if (!value) {
      return null;
    }
    sessionStorage.removeItem(PENDING_SIGN_IN_TYPE_KEY);
    if (value === 'password' || value === 'sso' || value === 'sign up') {
      return value;
    }
    return null;
  }

  static getLastSignInType(): SignInAnalyticsType | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const value = localStorage.getItem(LAST_SIGN_IN_TYPE_KEY);
    if (value === 'password' || value === 'sso' || value === 'sign up') {
      return value;
    }
    return null;
  }

  static clearLastSignInType(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem(LAST_SIGN_IN_TYPE_KEY);
  }

  constructor(private _env: Environment) {
    this.isAuthenticated().subscribe(authenticated => {
      if (authenticated) {
        window.addEventListener('beforeunload', () => {
          const logoutUrl = `https://${this._env.auth0.domain}/v2/logout?client_id=${this._env.auth0.clientId}&returnTo=${encodeURIComponent(window.location.origin)}`;
          new Image().src = logoutUrl;
        });
        this.notifyUserLoggedIn();
      } else {
        this.notifyUserLoggedOut();
      }
    });
    this._auth0Service.error$.subscribe((errors) => {
      if (this._reloginErrors.includes(errors.message)) {
        this.login('login', '', { trackSignInAnalytics: false });
      } else {
        /** errors logging in don't always result in the
         *  Auth0 information clearing, so we log out.
         */
        this.logout().subscribe();
      }
    });
  }

  getUserLoggedInEvent(): Observable<boolean> {
    return this._userLoggedInSubject.asObservable();
  }

  getUserLoggedOutEvent(): Observable<boolean> {
    return this._userLoggedOutSubject.asObservable();
  }

  notifyUserLoggedIn() {
    this._userLoggedInSubject.next(true);
    this._userLoggedOutSubject.next(false);
    this._bsHubService.dispatch('bsAuth', { event: 'signedIn' });
  }

  notifyUserLoggedOut() {
    this._userLoggedInSubject.next(false);
    this._userLoggedOutSubject.next(true);
    this._bsHubService.dispatch('bsAuth', { event: 'signedOut' });
  }

  login(
    prompt: 'login' | 'signup' | 'none' | string = 'login',
    additionalScope: string = '',
    options: LoginOptions = {}
  ): void {
    const { connection, trackSignInAnalytics = true } = options;
    const isSsoLogin = connection !== undefined;
    const connectionValue = connection?.trim() ?? '';
    this.overrideIsAuthenticated = null;

    if (trackSignInAnalytics) {
      this._trackSignInStarted(this._resolveSignInAnalyticsType(prompt, isSsoLogin));
    }

    const authorizationParams: Record<string, string> = {
      screen_hint: prompt,
      scope: this._defaultScope + ' ' + additionalScope,
      redirect_uri: window.location.origin + '/authenticating',
    };

    if (connectionValue) {
      authorizationParams.connection = connectionValue;
    }

    this._auth0Service.loginWithRedirect({
      ...(isSsoLogin ? { appState: { loginType: 'sso' } } : {}),
      authorizationParams,
    });
  }

  loginWithSso(connection: string): void {
    this.login('login', '', { connection, trackSignInAnalytics: true });
  }

  logout(): Observable<void> {
    const logoutOptions = {
      logoutParams: {
        returnTo: window.location.origin,
      },
    };

    return new Observable<void>((observer) => {
      const logoutSubscription = this._auth0Service.logout(logoutOptions).subscribe({
        next: () => {
          this.overrideIsAuthenticated = null;
          observer.next();
          observer.complete();
        },
        error: (error: any) => {
          observer.error(error);
          console.error('Error logging out', error);
        },
      });
      return () => {
        logoutSubscription.unsubscribe();
      };
    });
  }

  isAuthenticated(): Observable<boolean> {
    if (typeof this.overrideIsAuthenticated === 'boolean') {
      return of(this.overrideIsAuthenticated);
    }
    return this._auth0Service.isAuthenticated$;
  }

  getToken(): Observable<string> {
    return this.isAuthenticated().pipe(
      switchMap((authenticated) => {
        if (authenticated) {
          if (this.storedAccessToken) return of(this.storedAccessToken);
          return this._auth0Service
            .getAccessTokenSilently({
              authorizationParams: {
                scope: 'openid profile email phone_number read:current_user read:current_user_metadata offline_access',
              },
            })
            .pipe(
              map((token) => token ?? ''),
              catchError((error) => {
                console.error('Error retrieving token:', error);
                return of('');
              })
            );
        } else {
          return of('');
        }
      })
    );
  }

  getIdTokenClaims() {
    return this._auth0Service.idTokenClaims$;
  }

  getUserFromAuth0(): Observable<User | null | undefined> {
    return this._auth0Service.user$;
  }

  getAuth0UserId$(): Observable<string | undefined> {
    return this._auth0Service.user$.pipe(
      map((user) => user?.sub),
      filter((sub): sub is string => !!sub),
      take(1)
    );
  }

  fetchUserAttributes(force = false): Observable<UserAttributesInterface> {
    return this._profileService.getProfile(force);
  }

  setAccessTokenForMicroApps(accessToken: string) {
    this.storedAccessToken = accessToken;
    this.overrideIsAuthenticated = true;
    this.notifyUserLoggedIn();
  }

  changePassword() {
    this.login('login', 'change_password', { trackSignInAnalytics: false });
  }

  changePhoneNumber() {
    this.login('login', 'change_phone_number', { trackSignInAnalytics: false });
  }

  changeEmailAddress() {
    this.login('login', 'change_email_address', { trackSignInAnalytics: false });
  }

  private _resolveSignInAnalyticsType(prompt: string, isSsoLogin = false): SignInAnalyticsType {
    if (isSsoLogin) {
      return 'sso';
    }
    if (prompt === 'signup') {
      return 'sign up';
    }
    return 'password';
  }

  private _trackSignInStarted(type: SignInAnalyticsType): void {
    this._analytics.logEvent('sign_in_started', { type });
    sessionStorage.setItem(PENDING_SIGN_IN_TYPE_KEY, type);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LAST_SIGN_IN_TYPE_KEY, type);
    }
  }
}
